import { ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { IExam, IExamQuestionBank, IQuestionBankDto } from 'src/app/interface/dto';
import { ExamService } from 'src/app/services/exam-service.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { Location } from '@angular/common';
import { QuestionBankService } from 'src/app/questions-bank/question-bank.service';

@Component({
  selector: "app-edit-examination-question-bank",
  templateUrl: "./edit-examination-question-bank.component.html",
  styleUrls: ["./edit-examination-question-bank.component.scss"],
})
export class EditExaminationQuestionBankComponent implements OnInit, OnChanges {
  textDisplay = "Add New Question Bank";
  modelError = "";
  isSave = true;
  showWebTestQuestionBank = false;
  availableQuestionBanks: IQuestionBankDto[] = [];
  filteredQuestionBanks: IQuestionBankDto[] = [];
  selectedQuestionBankId: number = null;

  questionBank: IExamQuestionBank = {
    id: 0,
    examinationID: 0,
    questionBankID: -1,
    category: "",
    numberOfQuestions: 0,
    questionBankPoolSize: 0,
    questionBankSource: "",
    disabled: false,
    isWebTestBank: false,
    name: "",
    totalNumberOfQuestions: 0,
    isPsychometricTestBank: false
  };

  @Input() selectedQuestionBank: IExamQuestionBank;
  @Input() exam: IExam;

  get hasWebTestQuestionBank(): boolean {
    return this.availableQuestionBanks.some((qb) => qb.isWebTestBank);
  }

  constructor(
    private examService: ExamService,
    private snackBar: SnackbarService,
    private _location: Location,
    private cdr: ChangeDetectorRef,
    private questionBankService: QuestionBankService
  ) {}

  async ngOnInit() {
    if (!this.exam) {
      const storedExams = this.examService.selectedExams;
      if (storedExams && storedExams.length > 0) {
        this.exam = storedExams[0];
      }
    }

    await this.loadAvailableQuestionBanks();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (
      changes["selectedQuestionBank"] &&
      changes["selectedQuestionBank"].currentValue
    ) {
      this.questionBank = { ...changes["selectedQuestionBank"].currentValue };

      if (this.questionBank.id && this.questionBank.id > 0) {
        this.textDisplay = `${this.questionBank.isPsychometricTestBank ? '' : 'Update '}Exam Question Bank`;
        this.isSave = false;
        this.selectedQuestionBankId = this.questionBank.questionBankID;
        await this.getQuestionBankDetails();
      } else {
        this.textDisplay = "Add New Exam Question Bank";
        this.isSave = true;
        this.selectedQuestionBankId = null;
      }
    }
    this.cdr.detectChanges();
  }

  async loadAvailableQuestionBanks() {
    try {
      this.availableQuestionBanks =
        await this.questionBankService.getQuestionBanksLookup(
          this.exam?.examinationTypeenum,
          this.exam?.id
        );
      this.filterQuestionBanks();
    } catch (error) {
      console.error("Error loading question banks:", error);
      this.snackBar.error("Failed to load question banks");
    }
  }

  onShowWebTestQuestionBankChange() {
    this.filterQuestionBanks();

    if (this.showWebTestQuestionBank) {
      this.snackBar.success("WebTest Question Bank is now visible.");
    } else {
      this.snackBar.success("WebTest Question Bank has been hidden.");
    }
    
    // Reset selection if current selection is no longer visible
    if (this.selectedQuestionBankId) {
      const isVisible = this.filteredQuestionBanks.some(
        (qb) => qb.questionBankID === this.selectedQuestionBankId
      );
      if (!isVisible) {
        this.selectedQuestionBankId = null;
        this.onQuestionBankSelect(null);
      }
    }
  }

  filterQuestionBanks() {
    if (this.showWebTestQuestionBank) {
      this.filteredQuestionBanks = this.availableQuestionBanks;
    } else {
      this.filteredQuestionBanks = this.availableQuestionBanks.filter(
        (qb) => !qb.isWebTestBank
      );
    }
  }

  onQuestionBankSelect(questionBankId: number) {
    if (!questionBankId) {
      return;
    }

    const selectedBank = this.availableQuestionBanks.find(
      (qb) => qb.questionBankID === questionBankId
    );
    if (selectedBank) {
      this.questionBank.questionBankID = selectedBank.questionBankID;
      this.questionBank.category = selectedBank.category || "";
      this.questionBank.isWebTestBank = selectedBank.isWebTestBank;
    }
  }

  async getQuestionBankDetails() {
    if (!this.isSave && this.questionBank.id) {
      await this.examService
        .getExaminationQuestionBank(
          this.questionBank.examinationID,
          this.questionBank.id
        )
        .then((qb: any) => {
          this.questionBank = qb;
        });
    }
  }

  saveQuestionBank(update: boolean = false) {
    this.validateModel();

    if (this.modelError !== "") {
      window.document.getElementById("modelError")?.focus();
      window.document.getElementById("modelError")?.blur();
      return;
    }

    this.questionBank.examinationID = this.exam.id;

    if (update) {
      this.examService
        .updateExaminationQuestionBank(this.questionBank)
        .then((res: any) => {
          if (res?.success) {
            this.snackBar.success("Exam Question Bank Updated Successfully!");
            this.examService.reloadGrid.next(true);
          } else {
            this.snackBar.error(res.message || "Update failed");
          }
        })
        .catch((error) => {
          console.error("Exam Question Bank update error:", error);
          this.snackBar.error(error);
        });
    } else {
      this.examService
        .saveExaminationQuestionBank(this.questionBank)
        .then((res: any) => {
          if (res?.success) {
            this.snackBar.success("Question Bank Saved Successfully!");
            this.examService.reloadGrid.next(true);
          } else {
            this.snackBar.error(res.message || "Save failed");
          }
        })
        .catch((error) => {
          console.error("Exam Question Bank save error:", error);
          this.snackBar.error(`Exam Question Bank save error: ${error.message}`);
        });
    }
  }

  validateModel() {
    this.modelError = "";

    if (
      !this.questionBank.questionBankID ||
      this.questionBank.questionBankID === 0
    ) {
      this.modelError += "\n* Question Bank is required\n";
    }

    if (this.questionBank.numberOfQuestions < 0) {
      this.modelError += "\n* Question Bank Number of Questions cannot be less than zero\n";
    }

     if (this.questionBank.numberOfQuestions > this.questionBank.questionBankPoolSize) {
      this.modelError += "\n* Question Bank Number of Questions cannot be greater than Bank pool size\n";
    }


    if (this.questionBank.questionBankPoolSize < 0) {
      this.modelError += "\n* Question Bank Pool Size cannot be less than zero\n";
    }

    if (this.questionBank.questionBankPoolSize > 1000) {
      this.modelError += "\n* Question Bank Pool Size cannot exceed 1,000\n";
    }
  }

  cancel() {
    this._location.back();
  }

  onQuestionBankChange(selectedId: number) {
    const selectedQB = this.filteredQuestionBanks.find(
      (qb) => qb.questionBankID === selectedId
    );
    
    if (selectedQB) {
      this.questionBank.category = selectedQB.category || "";
      this.questionBank.isWebTestBank = selectedQB.isWebTestBank;
    } else {
      this.questionBank.category = "";
    }
  }
}
